/**
 * Not losing edits by leaving.
 *
 * A form that edits model fields holds its edits in the page and nowhere else:
 * reload, close the tab, or click a link elsewhere and they are gone. This is
 * the one place that knows whether anything is in that state, and what stops a
 * person walking away from it by accident:
 *
 *   - the browser's own "leave site?" prompt, registered ONLY while something is
 *     unsaved (a handler that is always there makes every reload nag)
 *   - a confirmation before an in-app navigation away from the form, through
 *     the router's blocker
 *   - the program picker's existing warning, fed through `setUnsaved` in the
 *     project state - which nothing set before this
 *
 * It is general, not about outages: it applies to any unsaved edit, always.
 *
 * Forms register with `useUnsavedGuard(dirty)`. Outside a provider that is a
 * no-op, so a form rendered on its own (in a test, say) needs nothing.
 *
 * A form that saves and then NAVIGATES in the same tick has to say it is done
 * first, with the `release` the hook returns. Its `dirty` is still true at that
 * moment - React has not re-rendered - and without the release the navigation to
 * the thing just saved is the one that gets blocked (issue #89: a new
 * requirement asked "Leave without saving?" on its way to its own page, and
 * "Stay" left the person on a list that did not have it yet).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom'
import { useProject } from './Project'

interface Unsaved {
  /** Say whether the form with this id holds unsaved edits. */
  report: (id: string, dirty: boolean) => void
}

const UnsavedContext = createContext<Unsaved | null>(null)

/**
 * The in-app half. Only mounted when the app runs under a data router, because
 * `useBlocker` throws under any other; under a plain router (a test's
 * MemoryRouter) in-app navigation is simply not intercepted, which is what it
 * always was.
 *
 * A FUNCTION, not this render's boolean: the router asks it at the moment of
 * navigating, so a guard released a line before `navigate()` is already gone.
 * A boolean is only as fresh as the last render.
 */
function NavigationBlocker({ isDirty }: { isDirty: () => boolean }) {
  const blocker = useBlocker(isDirty)
  if (blocker.state !== 'blocked') return null

  return (
    <Dialog open onClose={() => blocker.reset()}>
      <DialogTitle>Leave without saving?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This page has changes that have not been saved. If you leave, they are
          lost.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => blocker.reset()} autoFocus>
          Stay
        </Button>
        <Button color="warning" onClick={() => blocker.proceed()}>
          Leave
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function UnsavedProvider({ children }: { children: ReactNode }) {
  const { setUnsaved } = useProject()
  // Kept in a ref so the blocker can read it the instant a report lands; the
  // state beside it is what re-renders the prompt and the picker's warning.
  const dirtyIds = useRef(new Set<string>())
  const [dirty, setDirty] = useState(false)
  const dataRouter = useContext(UNSAFE_DataRouterContext)

  const report = useCallback((id: string, isDirty: boolean) => {
    const ids = dirtyIds.current
    if (ids.has(id) === isDirty) return
    if (isDirty) ids.add(id)
    else ids.delete(id)
    setDirty(ids.size > 0)
  }, [])

  const isDirty = useCallback(() => dirtyIds.current.size > 0, [])

  // The picker's warning before switching program is the same fact.
  useEffect(() => {
    setUnsaved(dirty)
  }, [dirty, setUnsaved])

  // The browser's prompt, registered only while there is something to lose.
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Required by some browsers to show the prompt at all.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const value = useMemo<Unsaved>(() => ({ report }), [report])

  return (
    <UnsavedContext.Provider value={value}>
      {children}
      {dataRouter ? <NavigationBlocker isDirty={isDirty} /> : null}
    </UnsavedContext.Provider>
  )
}

/**
 * Register a form's unsaved state. Call it with the form's `dirty`.
 *
 * Unregisters when the form unmounts, so navigating away (once confirmed) or
 * closing a dialog never leaves a phantom "unsaved" behind.
 *
 * Returns `release`: call it once the edits are SAVED and before navigating
 * away, so that navigation is not the one asked about. It holds for the rest of
 * the form's life - a released form is one on its way out.
 */
export function useUnsavedGuard(dirty: boolean): () => void {
  const context = useContext(UnsavedContext)
  const id = useId()
  const report = context?.report
  const released = useRef(false)

  useEffect(() => {
    report?.(id, dirty && !released.current)
    return () => report?.(id, false)
  }, [report, id, dirty])

  return useCallback(() => {
    released.current = true
    report?.(id, false)
  }, [report, id])
}
